import {
  getLanAdapterAddress,
  isLanAdapter,
  isSameUnit
} from '@/plugins/lan-adapter'

/// Converts the api unit type to a value assets and translations can use.
const parseUnitTypeForAssets = (apiType) => {
  switch (apiType) {
    case 'lan_adapter':
      return 'wlanadapter'
    case 'solar_pump':
      return 'solarpump'
  }

  return apiType
}

const parseSerialToMacAddress = (serial) => {
  let localValue = null
  if (serial?.length) {
    localValue = serial.match(/.{1,2}/g).join(':')
  }
  return localValue
}

const unitsFromQuery = (query, macAddressRequired) => {
  const results = []
  // The adapter model is optional: some product ranges allow a LAN adapter without one
  if (query.adapter_serial) {
    results.push({
      type: 'lan_adapter',
      material: query.adapter_model || null,
      serial: macAddressRequired
        ? parseSerialToMacAddress(query.adapter_serial)
        : query.adapter_serial
    })
  }
  if (query.indoor_model && query.indoor_serial) {
    results.push({
      material: query.indoor_model,
      serial: query.indoor_serial
    })
  }
  if (query.outdoor_model && query.outdoor_serial) {
    results.push({
      material: query.outdoor_model,
      serial: query.outdoor_serial
    })
  }
  return results
}

export default function(ctx, inject) {
  const startRegistration = async (gatewayDeviceId) => {
    const validInstallations = ctx.$storage.getUniversal(
      'registration.valid-installations'
    )
    if (validInstallations && validInstallations.length) {
      ctx.store.commit('registration/setFlow', 'existingInstallation')
    } else {
      ctx.store.commit('registration/setFlow', 'newInstallation')
    }
    if (gatewayDeviceId) {
      await ctx.store.dispatch(
        'registration/setGatewayDeviceId',
        gatewayDeviceId
      )
    }
    const nextScreen = await ctx.store.dispatch('registration/getNextScreen')
    return ctx.app.router.push({ name: nextScreen })
  }
  inject('startRegistration', startRegistration)
  ctx.$startRegistration = startRegistration

  inject('parseUnitTypeForAssets', parseUnitTypeForAssets)
  ctx.$parseUnitTypeForAssets = parseUnitTypeForAssets

  inject('unitsFromQuery', unitsFromQuery)
  ctx.$unitsFromQuery = unitsFromQuery

  inject('parseSerialToMacAddress', parseSerialToMacAddress)
  ctx.$parseSerialToMacAddress = parseSerialToMacAddress

  const parseCustomerDetails = () => {
    let customerDetails = []
    switch (ctx.$storage.getUniversal('account').country) {
      case 'IT':
      case 'ES':
        customerDetails = ['fiscal_number']
        break
      case 'GR':
        customerDetails = ['fiscal_number', 'tax_office']
        break
    }

    return customerDetails
  }

  inject('parseCustomerDetails', parseCustomerDetails)
  ctx.$parseCustomerDetails = parseCustomerDetails

  const startRegistrationWithUnits = async (units, gatewayDeviceId) => {
    await ctx.store.dispatch('registration/clearRegistration')
    ctx.$sentry.addBreadcrumb({
      category: 'registration',
      level: 'info',
      message: 'Start registration with units',
      data: {
        units
      }
    })

    try {
      if (gatewayDeviceId) {
        await ctx.store.dispatch(
          'registration/setGatewayDeviceId',
          gatewayDeviceId
        )
      }
      // get and store the user's info to prefill registration
      await ctx.store.dispatch('registration/getCustomerInfo')
      await ctx.store.dispatch('registration/classifyInstallations')
      await ctx.store.dispatch('registration/verifyProductRanges')

      const result = await ctx.$marlonApi.validateUnit(units)
      ctx.$sentry.addBreadcrumb({
        category: 'registration',
        level: 'info',
        message: 'validation result',
        data: {
          result
        }
      })

      if (result?.validation_status === 'valid') {
        const installationExistsInSbm = ctx.$checkIfUnitBelongsToInstallation(
          units[0]
        )
        if (installationExistsInSbm !== null) {
          // Add units from the sbm data that are not already in the scanned units collection
          for (const unit of installationExistsInSbm?.units) {
            await ctx.store.dispatch('registration/addScannedUnit', unit)
          }
          ctx.$storage.setUniversal(
            'scan-units.installation',
            installationExistsInSbm
          )
          if (installationExistsInSbm.service_partner) {
            ctx.store.dispatch(
              'registration/setServicePartner',
              installationExistsInSbm.service_partner
            )
          }
        } else {
          for (const unit of units) {
            // Add units to scanned units collection
            // Skip incomplete units. The overview decides later if a LAN adapter needs a model,
            // because the product range is not known yet.
            const isIncomplete = isLanAdapter(unit)
              ? !getLanAdapterAddress(unit)
              : !unit.material || !unit.serial
            if (isIncomplete) {
              ctx.$sentry.addBreadcrumb({
                category: 'registration',
                level: 'info',
                message: 'skipping incomplete unit',
                data: {
                  unit
                }
              })
              continue
            }
            await ctx.store.dispatch('registration/addScannedUnit', {
              serial: unit.serial,
              material: unit.material,
              type: unit.type
            })
          }
        }

        const overviewUnits = await ctx.store.dispatch(
          'registration/getUnitsForOverview'
        )
        const productRange = ctx.$storage.getUniversal(
          'scan-units.product-range'
        )
        const multipleProductRanges = ctx.$storage.getUniversal(
          'scan-units.multiple-product-ranges'
        )

        // Continue registration only if both product range and product range options or units are missing
        if ((productRange || multipleProductRanges) && overviewUnits.length) {
          ctx.store.commit(
            'registration/setFlow',
            installationExistsInSbm === null
              ? 'newInstallation'
              : 'existingInstallation'
          )
          ctx.store.commit(
            'registration/setCurrentScreen',
            'registration-scan-units-overview'
          )
          return ctx.app.router.push({
            name: 'registration-scan-units-overview',
            params: { preventBack: true }
          })
        }
        ctx.$sentry.addBreadcrumb({
          category: 'registration',
          level: 'info',
          message: 'None of the units were available for the overview'
        })
      } else if (result?.validation_status === 'already_registered') {
        ctx.$storage.setUniversal('scan-units.existing', result)
        ctx.store.commit('registration/setFlow', 'commissioningCode')
        ctx.store.commit(
          'registration/setCurrentScreen',
          'registration-scan-units-check-invitation-unit'
        )
        if (result?.resend_invitation_email) {
          return ctx.app.router.push({
            name: 'registration-scan-units-check-invitation-unit'
          })
        } else {
          return ctx.app.router.push({
            name: 'registration-scan-units-not-your-unit'
          })
        }
      }
    } catch (err) {
      ctx.$sentry.setExtra(
        'Unit validation has failed while registering units',
        err
      )
      ctx.$sentry.captureException(err)
    }

    // if we are here, unit validation has failed invalid_url
    ctx.$sentry.captureMessage(
      'Unit validation failed, unsupported flow',
      'error'
    )
    return ctx.app.router.push({
      name: 'landing',
      query: { state: 'invalid_url' }
    })
  }

  inject('startRegistrationWithUnits', startRegistrationWithUnits)
  ctx.$startRegistrationWithUnits = startRegistrationWithUnits

  const checkIfUnitBelongsToInstallation = (unit) => {
    // check if the unit belings to one of the (valid) installations we receive from POST customer-info
    const validInstallations = ctx.$storage.getUniversal(
      'registration.valid-installations'
    )
    if (!validInstallations || !validInstallations.length) {
      return null
    }
    for (const installation of validInstallations) {
      for (const installationUnit of installation.units) {
        if (isSameUnit(installationUnit, unit)) {
          return installation
        }
      }
    }
    return null
  }
  inject('checkIfUnitBelongsToInstallation', checkIfUnitBelongsToInstallation)
  ctx.$checkIfUnitBelongsToInstallation = checkIfUnitBelongsToInstallation
}
